import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Landmark, MapPin, Search, ShieldCheck } from "lucide-react";

export default function OntarioDirectory() {
  const [registry, setRegistry] = useState(null);
  const [wardsAvailable, setWardsAvailable] = useState([]);
  const [wyvMunis, setWyvMunis] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/public/geo/municipalities/registry").then((r) => setRegistry(r.data)).catch(() => setRegistry([]));
    api.get("/public/geo/wards-available").then((r) => setWardsAvailable(r.data)).catch(() => {});
    api.get("/public/municipalities").then((r) => setWyvMunis(r.data)).catch(() => {});
  }, []);

  const wyvSlugs = useMemo(() => new Set(wyvMunis.map((m) => m.slug)), [wyvMunis]);

  const filtered = useMemo(() => {
    if (!registry) return [];
    const query = q.trim().toLowerCase();
    const rows = query ? registry.filter((m) => m.name.toLowerCase().includes(query)) : registry;
    return rows.slice(0, 120);
  }, [registry, q]);

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Ontario Municipalities</Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="ontario-directory-title">
          Every Ontario municipality.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          All {registry ? registry.length : "…"} Ontario municipalities, each with its own election information page —
          boundaries, wards where open data exists, candidates and official links.
        </p>
      </div>

      <div className="mt-8 max-w-md relative">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          className="pl-9"
          placeholder="Search municipalities…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="ontario-directory-search"
          aria-label="Search municipalities"
        />
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ontario-directory-grid">
        {!registry && [1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-24" />)}
        {filtered.map((m) => (
          <Link key={m.slug} to={`/ontario/${m.slug}`} className="group block" data-testid={`ontario-dir-${m.slug}`}>
            <Card className="h-full hover:shadow-md hover:border-primary/40 transition-shadow duration-150">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <Landmark className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-heading font-semibold text-sm text-foreground group-hover:text-primary truncate">{m.name}</h2>
                    <p className="text-[11px] text-muted-foreground">{m.type}{m.upper_tier ? ` · ${m.upper_tier}` : ""}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {wardsAvailable.includes(m.slug) && (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                          <MapPin className="h-3 w-3 mr-0.5" /> Ward maps
                        </Badge>
                      )}
                      {wyvSlugs.has(m.slug) && (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-800 border-blue-200">
                          <ShieldCheck className="h-3 w-3 mr-0.5" /> WYV active
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
        {registry && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">No municipality matches that search.</p>
        )}
      </div>
      {registry && !q && registry.length > 120 && (
        <p className="text-xs text-muted-foreground mt-5">Showing the first 120 — use search to find any of the {registry.length} municipalities.</p>
      )}
    </div>
  );
}
